import React, { useEffect, useRef } from "react";
import { Palette, X, Check } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import UploadZone from "@/components/UploadZone";
import { api, mediaUrl } from "@/lib/api";
import { useAttachments } from "@/lib/useUploader";
import { tagOf } from "@/lib/useCpSession";

export const styleNo = (styles) => Math.max(0, ...styles.map((s) => Number((/_Style_(\d+)$/.exec(tagOf(s.ref)) || [])[1]) || 0)) + 1;
// Styles that feed a keyframe: the ones assigned to it, or every style when none are assigned to it.
export const stylesFor = (styles, frame) => { const mine = styles.filter((s) => (s.frames || []).includes(frame)); return (mine.length ? mine : styles).map((s) => s.ref); };

const Assign = ({ s, keyframes, onAssign }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" className="relative block" data-testid={`cg-style-${s.ref.id}`} title="Click to assign to keyframes">
        <img src={mediaUrl(s.ref.url)} alt="" className="h-16 w-24 object-cover rounded border border-zinc-700 hover:border-[#1e97f3]" />
        <span className="absolute bottom-0.5 left-0.5 rounded bg-black/70 px-1 text-[8px] leading-[12px] text-white" data-testid={`cg-style-badge-${s.ref.id}`}>{(s.frames || []).length ? `#${s.frames.map((f) => f + 1).join(" #")}` : "All"}</span>
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-60 bg-[#0E1114] border-zinc-800 text-zinc-100 p-2" data-testid={`cg-style-assign-${s.ref.id}`}>
      <div className="text-[11px] text-zinc-400 mb-1.5 font-mono truncate">@{tagOf(s.ref)} · assign to keyframe</div>
      {!keyframes.length && <p className="text-[11px] text-zinc-500">Mark keyframes first.</p>}
      {keyframes.map((k) => {
        const on = (s.frames || []).includes(k.frame);
        return (
          <button key={k.frame} type="button" onClick={() => onAssign(s.ref.id, k.frame)} data-testid={`cg-style-assign-${s.ref.id}-${k.frame}`}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-white/5 ${on ? "text-white" : "text-zinc-400"}`}>
            <span className={`h-4 w-4 rounded border flex items-center justify-center ${on ? "bg-[#1e97f3] border-[#1e97f3]" : "border-zinc-600"}`}>{on && <Check className="w-3 h-3" />}</span>
            Frame #{k.frame}{k.character ? ` · ${k.character}` : ""}
          </button>
        );
      })}
      <p className="text-[10px] text-zinc-500 mt-1.5">Not assigned anywhere = used for every keyframe without its own style.</p>
    </PopoverContent>
  </Popover>
);

export default function StylePanel({ projectId, styles, keyframes, stem, onUploaded, onAdd, onRemove, onAssign, onGenerate }) {
  const up = useAttachments(projectId, 10, onUploaded, true, "cg-styles");
  const next = useRef(0);
  const doneKey = up.items.filter((x) => x.id && !x.uploading).map((x) => x.id).join(",");
  useEffect(() => {
    const done = up.items.filter((x) => x.id && !x.uploading);
    if (!done.length) return;
    up.setRaw((cur) => cur.filter((x) => !done.some((d) => d.id === x.id)));
    next.current = Math.max(next.current, styleNo(styles));
    done.forEach(async (r) => {
      const { preview, uploading, progress, tid, ...ref } = r;
      if (!preview) { onAdd(ref); return; }
      const n = next.current++;
      const ext = (r.name || "").split(".").pop() || "png";
      try {
        const { data } = await api.patch(`/references/${r.id}`, { name: `${stem}_Style_${n}.${ext}` });
        onAdd({ ...ref, name: data.name });
      } catch { toast.error("Could not add that style image"); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneKey]);
  return (
    <div className="rounded-xl border border-[#1065c0]/40 bg-[#0B0F16] p-2.5 space-y-2" data-testid="cg-style-panel">
      <div className="text-xs text-white flex items-center gap-1.5"><Palette className="w-3.5 h-3.5 text-[#41a7f5]" />Style Panel <span className="text-zinc-500">· {styles.length} image{styles.length === 1 ? "" : "s"} · click one to assign</span></div>
      {!!styles.length && (
        <div className="flex flex-wrap gap-2" data-testid="cg-style-list">
          {styles.map((s) => (
            <div key={s.ref.id} className="relative">
              <Assign s={s} keyframes={keyframes} onAssign={onAssign} />
              <button type="button" onClick={() => onRemove(s.ref.id)} data-testid={`cg-style-remove-${s.ref.id}`}
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-black border border-zinc-700 text-zinc-400 hover:text-red-400 flex items-center justify-center"><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
      <UploadZone testid="cg-style-upload" title="Add style images" badge="drop, attach or paste" subtitle="Lighting, colour and materials come from these"
        accept="image/png,image/jpeg,image/webp" max={10} items={up.items} onFiles={up.add} onRemove={up.remove} />
      <button type="button" onClick={onGenerate} data-testid="cg-style-generate-open"
        className="w-full h-8 rounded-lg border border-[#1477d2]/60 text-[11px] text-white flex items-center justify-center gap-1.5 hover:bg-[#0747a1]/30"><Palette className="w-3.5 h-3.5" />Generate style image</button>
    </div>
  );
}
